import React, { useState, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  ReferenceLine, Label, Brush,
} from 'recharts';

/**
 * InteractiveChart — Polished recharts renderer for paper/blog sections.
 *
 * chartConfig props:
 *   chartType  — 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'radar'
 *                | 'grouped-bar' | 'stacked-bar' | 'horizontal-bar'
 *   data       — Array of { name, value, [value2], … }
 *   xKey       — X-axis key (default 'name')
 *   yLabel     — Optional Y-axis label
 *   xLabel     — Optional X-axis label
 *   series     — Array of { dataKey, color, label } for multi-series
 *   colors     — Hex color array (fallback palette)
 *   height     — Chart height in px (default 360)
 *   showBrush  — Show zoom brush bar (default false)
 *   showGrid   — Show grid lines (default true)
 *   showLegend — Show legend (default true)
 *   stacked    — Stack area/line series (default false)
 *   smooth     — Smooth curves on line/area (default true)
 *   caption    — Figure caption text
 *   title      — Chart title
 */

/* ── Default palette ─────────────────────────────────────── */
const DEFAULT_COLORS = [
  '#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6',
];

/* ── Helpers ──────────────────────────────────────────────── */
function abbreviateNum(v) {
  if (v == null || isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e4) return `${(v / 1e3).toFixed(1)}K`;
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toFixed(1);
}

function inferSeries(data) {
  if (!data?.length) return [];
  const keys = Object.keys(data[0]).filter(
    (k) => k !== 'name' && k !== 'label' && k !== 'color' && typeof data[0][k] === 'number'
  );
  if (keys.length === 1 && keys[0] === 'value') return [];
  return keys.map((k) => ({ dataKey: k, label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()) }));
}

function computeTotal(data) {
  return data.reduce((sum, d) => sum + (d.value || 0), 0);
}

/* ── Custom Tooltip ───────────────────────────────────────── */
function ChartTooltip({ active, payload, label, total }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ic-tooltip">
      {label != null && <p className="ic-tooltip-label">{label}</p>}
      {payload.map((p, i) => {
        const pct = total && typeof p.value === 'number' ? ((p.value / total) * 100).toFixed(1) : null;
        return (
          <div key={i} className="ic-tooltip-row">
            <span className="ic-tooltip-dot" style={{ background: p.color || p.fill || p.stroke }} />
            <span className="ic-tooltip-name">{p.name}</span>
            <span className="ic-tooltip-value">
              {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
              {pct && <span className="ic-tooltip-pct"> ({pct}%)</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Custom Legend ─────────────────────────────────────────── */
function ChartLegend({ payload }) {
  if (!payload?.length) return null;
  return (
    <div className="ic-legend">
      {payload.map((entry, i) => (
        <div key={i} className="ic-legend-item">
          <span className="ic-legend-dot" style={{ background: entry.color }} />
          <span className="ic-legend-text">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Angled Tick for long labels ──────────────────────────── */
function AngledTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="end" fill="currentColor" fontSize={10}
        transform="rotate(-35)" className="ic-axis-tick">
        {payload.value?.length > 14 ? payload.value.slice(0, 13) + '\u2026' : payload.value}
      </text>
    </g>
  );
}

/* ── Pie custom label ─────────────────────────────────────── */
const RADIAN = Math.PI / 180;
function renderPieLabel({ cx, cy, midAngle, outerRadius, name, percent }) {
  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.04) return null;
  return (
    <text x={x} y={y} fill="currentColor" textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central" fontSize={11} fontWeight={500} className="ic-pie-label">
      {name} ({(percent * 100).toFixed(0)}%)
    </text>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function InteractiveChart({ chartConfig, caption, figureNumber, themeColors }) {
  const chartRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  /* Extract config (with defaults) — must happen before hooks */
  const {
    chartType = 'bar',
    data = [],
    xKey = 'name',
    xLabel,
    yLabel,
    series: rawSeries,
    colors: configColors,
    height = 360,
    showBrush = false,
    showGrid = true,
    showLegend = true,
    smooth = true,
    stacked = false,
    title,
  } = chartConfig || {};

  /* Color priority: chartConfig.colors → themeColors → DEFAULT_COLORS */
  const colors = configColors || themeColors || DEFAULT_COLORS;

  /* Auto-detect series from data keys if not provided */
  const series = useMemo(
    () => (rawSeries?.length ? rawSeries : inferSeries(data)),
    [rawSeries, data]
  );

  const total = useMemo(() => computeTotal(data), [data]);

  /* Compute average for reference line on bar/line/area */
  const avgValue = useMemo(() => {
    if (series.length > 0) return null;
    const nums = data.map((d) => d.value).filter((v) => typeof v === 'number');
    return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
  }, [data, series]);

  /* ── Resolve theme CSS vars for inline SVG props ─────────── */
  const [cv, setCv] = useState({
    axis: '#94a3b8', tick: '#475569', axisLine: '#e2e8f0',
    grid: '#f1f5f9', bg: '#fff', title: '#1e293b',
  });
  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const s = getComputedStyle(el);
    const g = (n, fb) => s.getPropertyValue(n).trim() || fb;
    setCv({
      axis: g('--ic-axis', '#94a3b8'),
      tick: g('--ic-tick', '#475569'),
      axisLine: g('--ic-axis-line', '#e2e8f0'),
      grid: g('--ic-grid', '#f1f5f9'),
      bg: g('--ic-bg', '#fff'),
      title: g('--ic-title', '#1e293b'),
    });
  }, [themeColors]);

  /* ── Download as SVG ────────────────────────────────────── */
  const handleDownload = useCallback(() => {
    const container = chartRef.current;
    if (!container) return;
    const svg = container.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'chart').replace(/\s+/g, '-').toLowerCase()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [title]);

  /* Early return for empty state — AFTER all hooks */
  if (!chartConfig || !data.length) {
    return (
      <div className="ic-empty">
        <span>No chart data provided</span>
      </div>
    );
  }

  const needsAngledTick = data.some((d) => String(d[xKey] || '').length > 8);
  const curveType = smooth ? 'monotone' : 'linear';

  /* Shared axis props — use theme-resolved `cv` values */
  const xAxisProps = {
    dataKey: xKey,
    stroke: cv.axis,
    fontSize: 11,
    tick: needsAngledTick ? <AngledTick /> : { fill: cv.tick, fontSize: 11 },
    tickLine: false,
    axisLine: { stroke: cv.axisLine },
  };
  const yAxisProps = {
    stroke: cv.axis,
    fontSize: 11,
    tickFormatter: abbreviateNum,
    tickLine: false,
    axisLine: { stroke: cv.axisLine },
    tick: { fill: cv.tick },
    width: 48,
  };
  const gridProps = showGrid ? { strokeDasharray: '3 6', stroke: cv.grid, vertical: false } : null;
  /* Reusable label / cursor / brush / refline props */
  const themeLabel = { fill: cv.axis, fontSize: 10 };
  const themeCursor = { fill: cv.grid };
  const themeBrush = cv.axisLine;
  const legendProps = showLegend ? { content: <ChartLegend /> } : null;

  const renderChart = () => {
    const margins = { left: 4, right: 16, top: 12, bottom: needsAngledTick ? 40 : 8 };

    switch (chartType) {

      /* ── BAR CHART ──────────────────────────────────────── */
      case 'bar':
        return (
          <BarChart data={data} margin={margins} barCategoryGap="20%">
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis {...xAxisProps}>
              {xLabel && <Label value={xLabel} position="insideBottom" offset={-4} {...themeLabel} />}
            </XAxis>
            <YAxis {...yAxisProps}>
              {yLabel && <Label value={yLabel} angle={-90} position="insideLeft" offset={10} {...themeLabel} />}
            </YAxis>
            <Tooltip content={<ChartTooltip total={total} />} cursor={themeCursor} />
            {legendProps && <Legend {...legendProps} />}
            {avgValue != null && (
              <ReferenceLine y={avgValue} stroke={cv.axis} strokeDasharray="4 4">
                <Label value={`Avg: ${abbreviateNum(avgValue)}`} position="insideTopRight" {...themeLabel} />
              </ReferenceLine>
            )}
            {series.length > 0 ? (
              series.map((s, i) => (
                <Bar key={s.dataKey} dataKey={s.dataKey} name={s.label || s.dataKey}
                  fill={s.color || colors[i % colors.length]}
                  radius={[5, 5, 0, 0]}
                  animationDuration={800} animationBegin={i * 120}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  opacity={hoveredIndex == null || hoveredIndex === i ? 1 : 0.35}
                />
              ))
            ) : (
              <Bar dataKey="value" name="Value" radius={[6, 6, 0, 0]}
                animationDuration={800} animationBegin={0}>
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color || colors[i % colors.length]}
                    opacity={hoveredIndex == null || hoveredIndex === i ? 1 : 0.4}
                  />
                ))}
              </Bar>
            )}
            {showBrush && <Brush dataKey={xKey} height={24} stroke={themeBrush} travellerWidth={8} />}
          </BarChart>
        );

      /* ── HORIZONTAL BAR ─────────────────────────────────── */
      case 'horizontal-bar':
        return (
          <BarChart data={data} layout="vertical" margin={{ ...margins, left: 60 }} barCategoryGap="20%">
            {gridProps && <CartesianGrid {...gridProps} horizontal={false} vertical />}
            <XAxis type="number" stroke={cv.axis} fontSize={11} tickFormatter={abbreviateNum}
              tickLine={false} axisLine={{ stroke: cv.axisLine }} />
            <YAxis type="category" dataKey={xKey} stroke={cv.axis} fontSize={11}
              tickLine={false} axisLine={{ stroke: cv.axisLine }} tick={{ fill: cv.tick }} width={80} />
            <Tooltip content={<ChartTooltip total={total} />} cursor={themeCursor} />
            {legendProps && <Legend {...legendProps} />}
            {series.length > 0 ? (
              series.map((s, i) => (
                <Bar key={s.dataKey} dataKey={s.dataKey} name={s.label || s.dataKey}
                  fill={s.color || colors[i % colors.length]}
                  radius={[0, 5, 5, 0]}
                  animationDuration={800} animationBegin={i * 120} />
              ))
            ) : (
              <Bar dataKey="value" name="Value" radius={[0, 6, 6, 0]} animationDuration={800}>
                {data.map((d, i) => <Cell key={i} fill={d.color || colors[i % colors.length]} />)}
              </Bar>
            )}
          </BarChart>
        );

      /* ── GROUPED BAR ────────────────────────────────────── */
      case 'grouped-bar':
        return (
          <BarChart data={data} margin={margins} barCategoryGap="18%">
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<ChartTooltip />} cursor={themeCursor} />
            {legendProps && <Legend {...legendProps} />}
            {(series || []).map((s, i) => (
              <Bar key={s.dataKey} dataKey={s.dataKey} name={s.label || s.dataKey}
                fill={s.color || colors[i % colors.length]}
                radius={[4, 4, 0, 0]}
                animationDuration={800} animationBegin={i * 120}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                opacity={hoveredIndex == null || hoveredIndex === i ? 1 : 0.35}
              />
            ))}
            {showBrush && <Brush dataKey={xKey} height={24} stroke={themeBrush} />}
          </BarChart>
        );

      /* ── STACKED BAR ────────────────────────────────────── */
      case 'stacked-bar':
        return (
          <BarChart data={data} margin={margins} barCategoryGap="18%">
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<ChartTooltip />} cursor={themeCursor} />
            {legendProps && <Legend {...legendProps} />}
            {(series || []).map((s, i) => (
              <Bar key={s.dataKey} dataKey={s.dataKey} name={s.label || s.dataKey}
                fill={s.color || colors[i % colors.length]} stackId="a"
                radius={i === (series.length - 1) ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                animationDuration={800} animationBegin={i * 100} />
            ))}
            {showBrush && <Brush dataKey={xKey} height={24} stroke={themeBrush} />}
          </BarChart>
        );

      /* ── LINE CHART ─────────────────────────────────────── */
      case 'line':
        return (
          <LineChart data={data} margin={margins}>
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis {...xAxisProps}>
              {xLabel && <Label value={xLabel} position="insideBottom" offset={-4} {...themeLabel} />}
            </XAxis>
            <YAxis {...yAxisProps}>
              {yLabel && <Label value={yLabel} angle={-90} position="insideLeft" offset={10} {...themeLabel} />}
            </YAxis>
            <Tooltip content={<ChartTooltip />} />
            {legendProps && <Legend {...legendProps} />}
            {avgValue != null && (
              <ReferenceLine y={avgValue} stroke={cv.axis} strokeDasharray="4 4">
                <Label value={`Avg: ${abbreviateNum(avgValue)}`} position="insideTopRight" {...themeLabel} />
              </ReferenceLine>
            )}
            {series.length > 0 ? (
              series.map((s, i) => (
                <Line key={s.dataKey} type={curveType} dataKey={s.dataKey} name={s.label || s.dataKey}
                  stroke={s.color || colors[i % colors.length]} strokeWidth={2.5}
                  dot={{ r: 3.5, strokeWidth: 2, fill: cv.bg, stroke: s.color || colors[i % colors.length] }}
                  activeDot={{ r: 6, strokeWidth: 0, fill: s.color || colors[i % colors.length] }}
                  animationDuration={1000} animationBegin={i * 200}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  opacity={hoveredIndex == null || hoveredIndex === i ? 1 : 0.25}
                />
              ))
            ) : (
              <Line type={curveType} dataKey="value" name="Value"
                stroke={colors[0]} strokeWidth={2.5}
                dot={{ r: 3.5, strokeWidth: 2, fill: cv.bg, stroke: colors[0] }}
                activeDot={{ r: 6, strokeWidth: 0, fill: colors[0] }}
                animationDuration={1000} />
            )}
            {showBrush && <Brush dataKey={xKey} height={24} stroke={themeBrush} />}
          </LineChart>
        );

      /* ── AREA CHART ─────────────────────────────────────── */
      case 'area': {
        const areaSeries = series.length > 0 ? series : [{ dataKey: 'value', color: colors[0], label: 'Value' }];
        return (
          <AreaChart data={data} margin={margins}>
            <defs>
              {areaSeries.map((s, i) => {
                const c = s.color || colors[i % colors.length];
                return (
                  <linearGradient key={s.dataKey} id={`areaGrad-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={c} stopOpacity={0.02} />
                  </linearGradient>
                );
              })}
            </defs>
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis {...xAxisProps}>
              {xLabel && <Label value={xLabel} position="insideBottom" offset={-4} {...themeLabel} />}
            </XAxis>
            <YAxis {...yAxisProps}>
              {yLabel && <Label value={yLabel} angle={-90} position="insideLeft" offset={10} {...themeLabel} />}
            </YAxis>
            <Tooltip content={<ChartTooltip />} />
            {legendProps && <Legend {...legendProps} />}
            {areaSeries.map((s, i) => {
              const c = s.color || colors[i % colors.length];
              return (
                <Area key={s.dataKey} type={curveType} dataKey={s.dataKey} name={s.label || s.dataKey}
                  stroke={c} fill={`url(#areaGrad-${s.dataKey})`}
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 2, fill: cv.bg, stroke: c }}
                  activeDot={{ r: 5.5, strokeWidth: 0, fill: c }}
                  stackId={stacked ? 'stack' : undefined}
                  animationDuration={1000} animationBegin={i * 200} />
              );
            })}
            {showBrush && <Brush dataKey={xKey} height={24} stroke={themeBrush} />}
          </AreaChart>
        );
      }

      /* ── PIE / DONUT CHART ──────────────────────────────── */
      case 'pie': {
        const isDonut = chartConfig.donut !== false;
        return (
          <PieChart>
            <Pie data={data} cx="50%" cy="50%"
              innerRadius={isDonut ? '45%' : 0}
              outerRadius="75%"
              paddingAngle={data.length > 1 ? 3 : 0}
              dataKey="value"
              label={renderPieLabel}
              labelLine={{ stroke: cv.axisLine, strokeWidth: 1 }}
              animationDuration={900} animationEasing="ease-out"
              onMouseEnter={(_, i) => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color || colors[i % colors.length]}
                  opacity={hoveredIndex == null || hoveredIndex === i ? 1 : 0.45}
                  stroke={cv.bg} strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip total={total} />} />
            {isDonut && total > 0 && (
              <text x="50%" y="48%" textAnchor="middle" fill={cv.title} fontSize={22} fontWeight={700}>
                {abbreviateNum(total)}
              </text>
            )}
            {isDonut && (
              <text x="50%" y="56%" textAnchor="middle" fill={cv.axis} fontSize={11}>
                Total
              </text>
            )}
          </PieChart>
        );
      }

      /* ── SCATTER PLOT ───────────────────────────────────── */
      case 'scatter': {
        const xDataKey = series?.[0]?.dataKey || 'x';
        const yDataKey = series?.[1]?.dataKey || 'y';
        return (
          <ScatterChart margin={margins}>
            {gridProps && <CartesianGrid {...gridProps} />}
            <XAxis dataKey={xDataKey} name={series?.[0]?.label || 'X'} stroke={cv.axis} fontSize={11}
              tickLine={false} axisLine={{ stroke: cv.axisLine }} tickFormatter={abbreviateNum}>
              {xLabel && <Label value={xLabel} position="insideBottom" offset={-4} {...themeLabel} />}
            </XAxis>
            <YAxis dataKey={yDataKey} name={series?.[1]?.label || 'Y'} stroke={cv.axis} fontSize={11}
              tickLine={false} axisLine={{ stroke: cv.axisLine }} tickFormatter={abbreviateNum}>
              {yLabel && <Label value={yLabel} angle={-90} position="insideLeft" offset={10} {...themeLabel} />}
            </YAxis>
            <Tooltip content={<ChartTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={data} animationDuration={900}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.color || colors[i % colors.length]}
                  opacity={hoveredIndex == null || hoveredIndex === i ? 0.85 : 0.3}
                />
              ))}
            </Scatter>
          </ScatterChart>
        );
      }

      /* ── RADAR CHART ────────────────────────────────────── */
      case 'radar': {
        const radarSeries = series.length > 0 ? series : [{ dataKey: 'value', label: 'Value' }];
        return (
          <RadarChart data={data} outerRadius="70%">
            <PolarGrid stroke={cv.axisLine} strokeDasharray="3 3" />
            <PolarAngleAxis dataKey={xKey} fontSize={11} tick={{ fill: cv.tick, fontSize: 10 }} />
            <PolarRadiusAxis fontSize={9} stroke={cv.axisLine} tick={{ fill: cv.axis }}
              tickFormatter={abbreviateNum} />
            <Tooltip content={<ChartTooltip />} />
            {radarSeries.map((s, i) => {
              const c = s.color || colors[i % colors.length];
              return (
                <Radar key={s.dataKey} name={s.label || s.dataKey} dataKey={s.dataKey}
                  stroke={c} fill={c}
                  fillOpacity={hoveredIndex == null ? 0.18 : hoveredIndex === i ? 0.3 : 0.06}
                  strokeWidth={2}
                  animationDuration={1000} animationBegin={i * 200}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  dot={{ r: 3, fill: c, strokeWidth: 0 }}
                />
              );
            })}
            {legendProps && <Legend {...legendProps} />}
          </RadarChart>
        );
      }

      default:
        return <p style={{ textAlign: 'center', color: cv.axis }}>Unsupported chart type: {chartType}</p>;
    }
  };

  return (
    <div className="ic-container" ref={chartRef}>
      <div className="ic-header">
        <div className="ic-header-left">
          {figureNumber && <span className="ic-fig-num">Figure {figureNumber}</span>}
          {title && <h4 className="ic-title">{title}</h4>}
        </div>
        <button className="ic-download-btn" onClick={handleDownload} title="Download SVG">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
      </div>
      <div className="ic-chart-wrap">
        <ResponsiveContainer width="100%" height={height}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {caption && <p className="ic-caption">{caption}</p>}
    </div>
  );
}
